// Zahlarten-Verwaltung + GiroCode-Überweisung + Transaktionen
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Landmark, Check, X, Loader2, QrCode, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/payments`;

export function PaymentMethodsCard() {
  const [d, setD] = useState(null);
  const [bank, setBank] = useState({ holder: "", iban: "", bic: "", bank_name: "" });
  const [msg, setMsg] = useState("");
  const load = async () => { const r = await axios.get(`${API}/methods`); setD(r.data); setBank({ holder: "", iban: "", bic: "", bank_name: "", ...r.data.bank }); };
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const enabled = d.methods.filter((m) => m.enabled).map((m) => m.key);
  const save = async (en, b = bank) => {
    try { await axios.put(`${API}/methods`, { enabled: en, bank: b }); setMsg("✓ Gespeichert"); load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Fehler"); }
    setTimeout(() => setMsg(""), 3000);
  };
  const toggle = (k) => save(enabled.includes(k) ? enabled.filter((x) => x !== k) : [...enabled, k]);
  return (
    <div className="card space-y-3" data-testid="payment-methods-card">
      <div className="flex justify-between items-center"><div className="overline">Zahlarten</div>{msg && <span className="text-xs text-emerald-400" data-testid="pm-msg">{msg}</span>}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
        {d.methods.map((m) => (
          <button key={m.key} onClick={() => toggle(m.key)} data-testid={`pm-toggle-${m.key}`}
            className={`text-left p-3 rounded-md border transition-colors ${m.enabled ? "border-eb-primary bg-eb-primary/10" : "border-white/10 opacity-60"}`}>
            <div className="flex justify-between text-sm font-semibold">{m.label}{m.enabled ? <Check size={14} className="text-eb-primary" /> : <X size={14} />}</div>
            <div className="text-[10px] text-eb-muted">{m.provider === "stripe" ? "über Stripe Checkout" : "direkt auf Ihr Konto · ohne Gebühren"}</div>
            {m.needs_activation && <div className="text-[10px] text-amber-400 flex items-center gap-1 mt-1"><AlertTriangle size={10} /> {m.needs_activation}</div>}
          </button>
        ))}
      </div>
      <div className="overline flex items-center gap-1 pt-2"><Landmark size={12} /> Empfängerkonto für Überweisungen</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {[["holder", "Kontoinhaber"], ["iban", "IBAN"], ["bic", "BIC"], ["bank_name", "Bank (z. B. Sparkasse Vorpommern)"]].map(([k, l]) => (
          <input key={k} placeholder={l} value={bank[k] || ""} onChange={(e) => setBank({ ...bank, [k]: e.target.value })} data-testid={`pm-bank-${k}`} />
        ))}
      </div>
      <button className="btn btn-primary" onClick={() => save(enabled)} data-testid="pm-bank-save"><Check size={14} /> Konto speichern</button>
    </div>
  );
}

export function TransferModal({ booking, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.post(`${API}/bank-transfer`, { booking_id: booking.id }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Fehler"));
  }, [booking.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose} data-testid="transfer-modal">
      <div className="card max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between"><div className="font-heading font-bold flex items-center gap-2"><QrCode size={18} /> Überweisung mit GiroCode</div><button onClick={onClose} data-testid="transfer-close"><X size={16} /></button></div>
        {err && <div className="text-rose-400 text-sm" data-testid="transfer-error">{err}</div>}
        {!d && !err && <Loader2 className="animate-spin" />}
        {d && (
          <>
            <img src={`data:image/png;base64,${d.qr_png_b64}`} alt="GiroCode" className="w-52 h-52 mx-auto bg-white p-2 rounded" data-testid="transfer-qr" />
            <div className="text-[11px] text-eb-muted text-center">Mit der Banking-App scannen (Sparkasse, Volksbank, Deutsche Bank, ING, N26 …)</div>
            <div className="font-mono text-xs space-y-1 eb-surface p-3 rounded">
              <div>Empfänger: {d.holder}</div><div data-testid="transfer-iban">IBAN: {d.iban}</div>{d.bic && <div>BIC: {d.bic}</div>}
              <div>Betrag: € {d.amount_eur.toFixed(2)}</div><div data-testid="transfer-reference">Verwendungszweck: {d.reference}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function TransactionsCard() {
  const [tx, setTx] = useState([]);
  const load = async () => setTx((await axios.get(`${API}/transactions`)).data);
  useEffect(() => { load(); }, []);
  const confirm = async (ref) => { await axios.post(`${API}/bank-transfer/${ref}/confirm`); load(); };
  if (!tx.length) return null;
  return (
    <div className="card !p-0 overflow-hidden" data-testid="transactions-card">
      <div className="overline px-4 py-3 border-b border-white/5">Zahlungseingänge</div>
      {tx.slice(0, 15).map((t) => (
        <div key={t.session_id} className="flex justify-between items-center px-4 py-2 border-t border-white/5 text-xs" data-testid={`tx-${t.session_id}`}>
          <span className="font-mono truncate max-w-[220px]">{t.reference || t.session_id.slice(0, 18)}</span>
          <span className="text-eb-muted">{t.provider === "bank_transfer" ? "Überweisung" : `Stripe · ${(t.methods || ["card"]).join(", ")}`}</span>
          <span className="font-mono">€ {Number(t.amount).toFixed(2)}</span>
          {t.payment_status === "paid" ? <span className="text-emerald-400">bezahlt</span> :
            t.provider === "bank_transfer" ? <button className="btn !py-0.5 !px-2 text-[11px]" onClick={() => confirm(t.reference)} data-testid={`tx-confirm-${t.reference}`}>Eingang bestätigen</button> :
              <span className="text-amber-400">offen</span>}
        </div>
      ))}
    </div>
  );
}
